import { type FormEventHandler, memo } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { GdprBeforeOfferPrivacyLink } from '@/components/GdprBeforeOfferPrivacyLink'
import { PersonalNumberField } from '@/components/PersonalNumberField/PersonalNumberField'
import { GOTO_NEXT_SECTION, useActiveFormSectionId } from '@/features/priceCalculator/useActiveFormSectionId'
import { useShopSessionCustomer } from '@/features/priceCalculator/useShopSessionCustomer'
import { useShopSessionCustomerUpdateMutation } from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { useShopSessionId } from '@/services/shopSession/ShopSessionContext'
import { useErrorMessage } from '@/utils/useErrorMessage'
import { Button } from 'ui/src/components/Button/Button'
import { yStack } from 'ui/src/patterns/stack'

const SsnFieldName = 'ssn'

export const SsnSeSection = memo(({ className }: { className?: string }) => {
  const shopSessionId = useShopSessionId()!
  const shopSessionCustomer = useShopSessionCustomer()
  const { setActiveFormSectionId } = useActiveFormSectionId()
  const [updateCustomer, result] = useShopSessionCustomerUpdateMutation({
    // priceIntent.suggestedData may be updated based on customer.ssn
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    onCompleted() {
      setActiveFormSectionId(GOTO_NEXT_SECTION)
    },
    onError(error) {
      logger.debug(`Failed to update customer ssn: ${error.message}`, { error })
    },
  })

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    const ssn = event.currentTarget[SsnFieldName].value
    if (typeof ssn !== 'string') throw new Error('No SSN found in SSN section form')
    updateCustomer({ variables: { input: { shopSessionId: shopSessionId, ssn } } })
  }

  const { t } = useTranslation('purchase-form')
  const errorMessage = useErrorMessage(result.error)

  return (
    <form className={clsx(yStack({ gap: errorMessage ? 'md' : 'xxs' }), className)} onSubmit={handleSubmit}>
      <PersonalNumberField
        label={t('FIELD_SSN_SE_LABEL')}
        name={SsnFieldName}
        defaultValue={shopSessionCustomer?.ssn ?? ''}
        required={true}
        warning={!!errorMessage}
        message={errorMessage}
      />

      <Button type="submit" size="large" loading={result.loading} fullWidth={true}>
        {t('SUBMIT_LABEL_PROCEED')}
      </Button>

      <GdprBeforeOfferPrivacyLink />
    </form>
  )
})
SsnSeSection.displayName = 'SsnSeSection'

export const SSN_SE_SECTION_ID = 'ssn-se'
